import { Box, CollapsibleCard, Divider, Switch } from '@atgfr8/atg-ui-tailwind';
import { InformationIcon } from '@atgfr8/icons/Information';
import { ltlCustomerSetting } from '@store/customers/customerDetails/LTL';
import { Accessor, createSignal, Setter, Show } from 'solid-js';

import { InitialLTLFormValues } from '../Ltl';

export const GAP_COMPRESSION_MIN = 0;
export const GAP_COMPRESSION_MAX = 100;
const GAP_TRIGGER_THRESHOLD_PERCENT = 20;
const EXAMPLE_LOWBALL_RATE = 100;
const EXAMPLE_NEXT_BEST_RATE = 200;

const formatExampleRate = (rate: number) =>
  `$${Number.isInteger(rate) ? rate : rate.toFixed(2)}`;

export const getGapCompressionError = (values: InitialLTLFormValues) => {
  if (!values.enableLtlGapLogic) return null;
  const percentage = values.gapCompressionPercentage;
  if (
    percentage === null ||
    Number.isNaN(percentage) ||
    percentage < GAP_COMPRESSION_MIN ||
    percentage > GAP_COMPRESSION_MAX
  ) {
    return `Gap compression must be between ${GAP_COMPRESSION_MIN} and ${GAP_COMPRESSION_MAX}.`;
  }
  return null;
};

const GapLogic = (props: {
  setFields: (key: string, value: unknown) => void;
  data: Accessor<InitialLTLFormValues>;
  setIsDirty: Setter<boolean>;
  setIsSettingsDirty: Setter<boolean>;
  onSectionChange: () => void;
}) => {
  const [isPercentageTouched, setIsPercentageTouched] = createSignal(false);
  const error = () =>
    isPercentageTouched() ? getGapCompressionError(props.data()) : null;

  const examplePreview = () => {
    const percentage = props.data().gapCompressionPercentage;
    if (percentage === null || getGapCompressionError(props.data())) {
      return 'Enter a valid compression percentage to preview.';
    }
    const compressedRate =
      EXAMPLE_LOWBALL_RATE +
      ((EXAMPLE_NEXT_BEST_RATE - EXAMPLE_LOWBALL_RATE) * percentage) / 100;
    return `Carrier A ${formatExampleRate(EXAMPLE_LOWBALL_RATE)} > ${formatExampleRate(compressedRate)} (next best: Carrier B ${formatExampleRate(EXAMPLE_NEXT_BEST_RATE)})`;
  };

  const updateField = (
    key: 'enableLtlGapLogic' | 'gapCompressionPercentage',
    value: boolean | number | null,
  ) => {
    props.setIsDirty(true);
    props.setIsSettingsDirty(true);
    props.onSectionChange();
    props.setFields(key, value);
  };

  const onPercentageInput = (raw: string) => {
    setIsPercentageTouched(true);
    updateField('gapCompressionPercentage', raw === '' ? null : Number(raw));
  };

  return (
    <CollapsibleCard
      startTitle="Gap logic (lowball rate protection)"
      loading={ltlCustomerSetting.isLoading}
      class="mb-2"
    >
      <Box class="text-sm font-medium">
        Close the gap between a lowball carrier rate and the next best market
        rate by adding a portion back in as margin, before markups apply.
      </Box>
      <Box class="flex items-center gap-3 mt-4">
        <Box>Enable Gap Logic for this customer</Box>
        <Switch
          aria-label="Enable Gap Logic for this customer"
          dataTestId="gap-logic-enable-switch"
          checked={props.data().enableLtlGapLogic}
          onChange={(checked: boolean) =>
            updateField('enableLtlGapLogic', checked)
          }
        />
      </Box>
      <Box class="text-xs text-[#606060] mt-1">
        Off by default. Applies before cost-plus margin.
      </Box>
      <Show when={props.data().enableLtlGapLogic}>
        <Divider class="mt-2! mb-4!" />
        <Box class="flex flex-wrap items-center gap-4">
          <input
            type="range"
            min={GAP_COMPRESSION_MIN}
            max={GAP_COMPRESSION_MAX}
            step="1"
            aria-label="Gap compression percentage slider"
            data-testid="gap-logic-compression-slider"
            class="w-full max-w-[480px] accent-[#1B4960]"
            value={props.data().gapCompressionPercentage ?? GAP_COMPRESSION_MIN}
            onInput={(e) => onPercentageInput(e.currentTarget.value)}
          />
          <Box class="flex items-center gap-1">
            <input
              type="number"
              min={GAP_COMPRESSION_MIN}
              max={GAP_COMPRESSION_MAX}
              aria-label="Gap compression percentage"
              aria-invalid={Boolean(error())}
              data-testid="gap-logic-compression-input"
              class={`border rounded px-2 py-1 text-sm w-20 ${error() ? 'border-red-500' : 'border-[#ccc]'}`}
              value={props.data().gapCompressionPercentage ?? ''}
              onInput={(e) => onPercentageInput(e.currentTarget.value)}
            />
            <Box class="text-sm">%</Box>
          </Box>
          <Box class="flex items-center gap-2 text-sm text-[#606060]">
            <InformationIcon class="shrink-0 text-[#1B4960]" />
            Trigger threshold: {GAP_TRIGGER_THRESHOLD_PERCENT}% variance from
            next-lowest carrier rate (global setting)
          </Box>
        </Box>
        <Show when={error()}>
          <Box class="text-xs text-red-600 mt-1">{error()}</Box>
        </Show>
        <Box class="mt-4 px-3 py-2 text-sm bg-[#E8F0FE]">
          <span class="font-semibold">Example preview:</span> {examplePreview()}
        </Box>
      </Show>
    </CollapsibleCard>
  );
};

export default GapLogic;
